import { PRODUCT_MARKETPLACE_STATE, type ProductOverview } from "@nova-ai/product-detail-contract"
import { ChevronDown } from "lucide-react"

import { humanizeMarketplaceId } from "../../lib/marketplace"
import { formatDateTime } from "../../lib/format-date"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { ProductMarketplaceLinks } from "./product-marketplace-links"

export function ProductMarketplaceDetails({
  entry,
}: {
  entry: ProductOverview["marketplaces"][number]
}) {
  return (
    <section
      aria-label={`${humanizeMarketplaceId(entry.marketplace)} listing`}
      className="space-y-3"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <MarketplaceMark marketplace={entry.marketplace} />

        <h2 className="text-sm font-medium text-foreground">
          {humanizeMarketplaceId(entry.marketplace)}
        </h2>

        <StatusBadge tone={MARKETPLACE_STATE_TONE[entry.state]} size="sm">
          {MARKETPLACE_STATE_LABEL[entry.state]}
        </StatusBadge>

        {entry.isStale ? (
          <StatusBadge tone="warning" size="sm">
            Stale
          </StatusBadge>
        ) : null}

        {entry.isSetAside ? (
          <StatusBadge tone="warning" size="sm">
            Set aside
          </StatusBadge>
        ) : null}

        {entry.isDelisted ? (
          <StatusBadge tone="muted" size="sm">
            Inactive
          </StatusBadge>
        ) : null}
      </div>

      <ProductMarketplaceLinks link={entry.link} />

      <MarketplaceStateEvidence explanation={entry.explanation} />
    </section>
  )
}

function MarketplaceStateEvidence({
  explanation,
}: {
  explanation: ProductOverview["marketplaces"][number]["explanation"]
}) {
  if (!explanation.reason && !explanation.lastCovering && !explanation.staledBy) {
    return null
  }
  return (
    <details className="group/evidence rounded-lg bg-card text-xs text-muted-foreground">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-lg px-3 py-2 font-medium text-foreground outline-none hover:bg-muted/55 focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        {explanation.stale ? "Why this is stale" : "Import evidence"}

        <ChevronDown className="size-3.5 shrink-0 transition-transform group-open/evidence:rotate-180 motion-reduce:transition-none" />
      </summary>

      <div className="space-y-3 border-t border-border/60 px-3 py-3">
        {explanation.reason ? <p>{explanation.reason}</p> : null}

        {explanation.staledBy ? (
          <ImportEvidence title="Staled by" evidence={explanation.staledBy} />
        ) : null}

        {explanation.lastCovering ? (
          <ImportEvidence title="Last covered by" evidence={explanation.lastCovering} />
        ) : null}
      </div>
    </details>
  )
}

function ImportEvidence({
  title,
  evidence,
}: {
  title: string
  evidence: NonNullable<ProductOverview["marketplaces"][number]["explanation"]["lastCovering"]>
}) {
  const scope = coverageScopeLabel(evidence.coverage.kind, evidence.coverage.key)
  return (
    <section aria-label={title}>
      <h3 className="font-semibold text-foreground">{title}</h3>

      <dl className="mt-1 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1">
        <dt>Import</dt>

        <dd className="truncate font-mono text-foreground" title={evidence.marketplaceImportId}>
          {evidence.marketplaceImportId}
        </dd>

        <dt>Artifact</dt>

        <dd className="font-mono text-foreground">{evidence.feedArtifactId}</dd>

        <dt>Scope</dt>

        <dd className="text-foreground">
          {`${scope} · ${evidence.coverage.complete ? "complete" : "partial"}`}
        </dd>

        <dt>Run</dt>

        <dd className="truncate text-foreground" title={evidence.run?.slug ?? undefined}>
          {evidence.run ? `${evidence.run.slug ?? "Deleted run"} (#${evidence.run.id})` : "No run"}
        </dd>

        <dt>Terminal</dt>

        <dd className="text-foreground">{formatDateTime(evidence.terminalAt)}</dd>
      </dl>
    </section>
  )
}

function coverageScopeLabel(kind: string, key: string | null): string {
  if (kind === "master_category") {
    return `Master category: ${key ?? "unknown"}`
  }
  return kind === "explicit_skus" ? "Explicit SKUs" : "Whole marketplace"
}

const MARKETPLACE_STATE_LABEL = {
  [PRODUCT_MARKETPLACE_STATE.listed]: "Listed",
  [PRODUCT_MARKETPLACE_STATE.nok]: "NOK",
  [PRODUCT_MARKETPLACE_STATE.pending]: "Pending",
} as const

const MARKETPLACE_STATE_TONE = {
  [PRODUCT_MARKETPLACE_STATE.listed]: "success",
  [PRODUCT_MARKETPLACE_STATE.nok]: "destructive",
  [PRODUCT_MARKETPLACE_STATE.pending]: "muted",
} as const satisfies Record<keyof typeof MARKETPLACE_STATE_LABEL, BadgeTone>
